import React, { useEffect, useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import NavBar from '@/components/NavBar';
import Footer from '@/components/Footer';
import { useAuth } from '@/contexts/AuthContext';
import { useUserProfile } from '@/hooks/useUserProfile';
import { mockSongsData } from '@/data/mockSongsData';
import { Song, CreatorSequence } from '@/types/sequence';
import { Display } from '@/types/display';
import { getCreatorSequences } from '@/api/sequences';
import { toast } from 'sonner';
import { useCharity } from '@/hooks/useCharity';
import { useUserTabCounts } from '@/hooks/useUserTabCounts';
import { supabase } from '@/integrations/supabase/client';
import { Charity } from '@/types/charity';
import { generateUniqueUsername, extractUsernameFromEmail } from '@/lib/usernameUtils';

// Import components
import UserProfileHeader from '@/components/profile/UserProfileHeader';
import UserProfileForm, { ProfileFormValues } from '@/components/profile/UserProfileForm';
import DisplayTab from '@/components/profile/DisplayTab';
import SongsTab from '@/components/profile/SongsTab';
import PublicSongsTab from '@/components/profile/PublicSongsTab';
import CharityTab from '@/components/profile/CharityTab';
import ProfileSequencesTab from '@/components/profile/ProfileSequencesTab';
import { CheckInsTab } from '@/features/checkins/components/CheckInsTab';

// Local Song interface that matches the component's usage
interface LocalSong {
  id: string;
  title: string;
  artist: string;
  duration?: string;
  year?: number;
  genre?: string;
  albumCover?: string;
  album_cover_url?: string;
  videoUrl?: string;
}

interface UserProfile {
  id: string;
  user_id: string;
  username: string;
  display_name: string;
  bio?: string;
  avatar_url?: string;
  is_public: boolean;
  created_at: string;
  updated_at: string;
}

const UserProfile: React.FC = () => {
  const { username } = useParams<{ username: string }>();
  const navigate = useNavigate();
  const { user, isLoading } = useAuth();
  const { profile, loading: isProfileLoading, refreshProfile } = useUserProfile();
  
  // Profile state for public views
  const [publicProfileUser, setPublicProfileUser] = useState<UserProfile | null>(null);
  const [isPublicView, setIsPublicView] = useState(false);
  
  // UI state
  const [isEditing, setIsEditing] = useState(false);
  const [activeTab, setActiveTab] = useState("display");
  const navigationCheckRef = React.useRef<((callback: () => void) => void) | null>(null);

  // Data state
  const [userDisplay, setUserDisplay] = useState<Display | null>(null);
  const [userSongs, setUserSongs] = useState<Record<number, LocalSong[]>>({});
  const [userSequences, setUserSequences] = useState<CreatorSequence[]>([]);
  
  // Charity state and hooks
  const [isEditingCharity, setIsEditingCharity] = useState(false);
  const currentProfile = isPublicView ? publicProfileUser : profile;
  const { charity, isLoading: isCharityLoading, setCharity } = useCharity(currentProfile?.user_id);
  
  // Tab counts hook
  const { counts: tabCounts, isLoading: isCountsLoading } = useUserTabCounts(currentProfile?.user_id);

  const isOwner = user && currentProfile && user.id === currentProfile.user_id;

  // Public profile lookup
  const fetchPublicProfile = async (targetUsername: string) => {
    console.log('Fetching profile for username:', targetUsername);
    try {
      // First try exact match
      let { data, error } = await supabase
        .from('user_profiles')
        .select('*')
        .eq('username', targetUsername)
        .eq('is_public', true)
        .maybeSingle();

      // If no exact match found, try case-insensitive search
      if (!data && !error) {
        console.log('No exact match, trying case-insensitive search...');
        ({ data, error } = await supabase
          .from('user_profiles')
          .select('*')
          .ilike('username', targetUsername)
          .eq('is_public', true)
          .maybeSingle());
      }

      // If still no match, try removing spaces from stored usernames and comparing
      if (!data && !error) {
        console.log('No case-insensitive match, trying space-removed search...');
        const { data: allData, error: allError } = await supabase
          .from('user_profiles')
          .select('*')
          .eq('is_public', true);

        if (allError) {
          error = allError;
        } else if (allData && Array.isArray(allData)) {
          // Find a match by removing spaces and comparing
          const normalizedTarget = targetUsername.toLowerCase().replace(/\s+/g, '');
          const match = allData.find(profile => 
            profile.username.toLowerCase().replace(/\s+/g, '') === normalizedTarget
          );
          
          if (match) {
            data = match;
            error = null;
          } else {
            data = null;
          }
        }
      }

      if (error) {
        console.error('Error fetching profile:', error);
        toast.error('Failed to load profile');
        navigate('/');
        return;
      }

      if (!data) {
        console.log('No profile found for username:', targetUsername);
        toast.error('Profile not found');
        navigate('/');
        return;
      }

      console.log('Profile found:', data);
      setPublicProfileUser(data);
      
      // Fetch songs for this profile using proper JOIN
      try {
        // First get display years for this user
        const { data: displayYears, error: displayYearsError } = await supabase
          .from('display_years')
          .select('*')
          .eq('user_id', data.user_id);

        if (displayYearsError) {
          console.error('Error fetching display years:', displayYearsError);
        } else if (displayYears && displayYears.length > 0) {
          // Get songs for all display years
          const displayYearIds = displayYears.map(dy => dy.id);
          const { data: songs, error: songsError } = await supabase
            .from('display_songs')
            .select('*')
            .in('display_year_id', displayYearIds)
            .order('created_at', { ascending: false });

          if (songsError) {
            console.error('Error fetching songs:', songsError);
            setUserSongs({});
          } else {
            // Group songs by year (similar to fetchUserSongs logic)
            const songsByYear: Record<number, any[]> = {};
            songs?.forEach(song => {
              const year = song.year_introduced;
              if (!songsByYear[year]) {
                songsByYear[year] = [];
              }

              // Map database fields to component properties
              songsByYear[year].push({
                id: song.id,
                title: song.title,
                artist: song.artist,
                duration: song.duration || '0:00',
                year: song.year_introduced,
                genre: song.genre || undefined,
                albumCover: song.album_cover_url || undefined,
                album_cover_url: song.album_cover_url || undefined,
                videoUrl: song.video_url || undefined
              });
            });
            setUserSongs(songsByYear);
          }
        } else {
          setUserSongs({});
        }
      } catch (error) {
        console.error('Error fetching songs for public profile:', error);
        setUserSongs({});
      }

      // Fetch sequences for this profile
      try {
        const creatorData = await getCreatorSequences(data.user_id);
        setUserSequences(creatorData.sequences);
        console.log('Fetched public profile sequences:', creatorData.sequences);
      } catch (error) {
        console.error('Error fetching public profile sequences:', error);
        setUserSequences([]);
      }

      // Fetch display for this profile
      try {
        const currentYear = new Date().getFullYear();
        const { data: displayData, error: displayError } = await supabase
          .from('displays')
          .select(`
            *,
            display_years!left(
              id,
              year,
              display_songs(count)
            )
          `)
          .eq('user_id', data.user_id)
          .order('created_at', { ascending: false })
          .limit(1);

        if (displayError) {
          console.error('Error fetching display:', displayError);
          setUserDisplay(null);
        } else if (displayData && displayData.length > 0) {
          const display = displayData[0];
          const displayYears = (display as any).display_years || [];
          const currentYearData = displayYears.find((dy: any) => dy.year === currentYear);
          const mostRecentYear = displayYears.sort((a: any, b: any) => b.year - a.year)[0];
          const yearData = currentYearData || mostRecentYear;
          (display as any).songCount = yearData?.display_songs?.[0]?.count || 0;
          setUserDisplay(display);
          console.log('Fetched public profile display:', display);
        } else {
          setUserDisplay(null);
          console.log('No display found for public profile');
        }
      } catch (error) {
        console.error('Error fetching public profile display:', error);
        setUserDisplay(null);
      }
    } catch (err) {
      console.error('Error fetching profile:', err);
      toast.error('Failed to load profile');
      navigate('/');
    }
  };

  // Load profile data
  useEffect(() => {
    const loadProfile = async () => {
      if (username) {
        // Public view: fetch user by username
        setIsPublicView(true);
        setActiveTab("display");
        await fetchPublicProfile(username);
      } else if (user) {
        // Private view: use current user's profile from hook
        setIsPublicView(false);
        setActiveTab("display");

        // The useUserProfile hook handles profile creation automatically
        // Just fetch the user's displays and songs
        await fetchUserData(user.id);
      } else {
        navigate('/');
      }
    };

    if (!isLoading) {
      loadProfile();
    }
  }, [username, user, isLoading, navigate]);

  const fetchUserData = async (userId: string) => {
    await Promise.all([
      fetchDisplays(userId),
      fetchUserSongs(userId),
      fetchUserSequences(userId)
    ]);
  };

  const fetchDisplays = async (userId: string) => {
    try {
      const currentYear = new Date().getFullYear();

      const { data, error } = await supabase
        .from('displays')
        .select(`
          *,
          display_years!left(
            id,
            year,
            display_songs(count)
          )
        `)
        .eq('user_id', userId)
        .order('created_at', { ascending: false});

      if (error) {
        console.error('Error fetching displays:', error);
        setUserDisplay(null);
      } else {
        if (data && data.length > 0) {
          const display = data[0];
          // Find the current year's data or fallback to most recent year
          const displayYears = (display as any).display_years || [];
          const currentYearData = displayYears.find((dy: any) => dy.year === currentYear);
          const mostRecentYear = displayYears.sort((a: any, b: any) => b.year - a.year)[0];
          const yearData = currentYearData || mostRecentYear;

          // Attach the song count from the selected year
          (display as any).songCount = yearData?.display_songs?.[0]?.count || 0;

          setUserDisplay(display);
        } else {
          setUserDisplay(null);
        }
      }
    } catch (error) {
      console.error('Unexpected error fetching displays:', error);
      setUserDisplay(null);
    }
  };

  const fetchUserSongs = async (userId: string) => {
    try {
      // Get display years (old system) and display holiday years (new system)
      const [displayYearsResult, displayHolidayYearsResult] = await Promise.all([
        supabase
          .from('display_years')
          .select('*')
          .eq('user_id', userId),
        supabase
          .from('display_holiday_years')
          .select('*')
          .eq('user_id', userId)
      ]);

      if (displayYearsResult.error) {
        console.error('Error fetching display years:', displayYearsResult.error);
      }

      if (displayHolidayYearsResult.error) {
        console.error('Error fetching display holiday years:', displayHolidayYearsResult.error);
      }

      const displayYears = displayYearsResult.data || [];
      const displayHolidayYears = displayHolidayYearsResult.data || [];

      // If no display years at all, return empty
      if (displayYears.length === 0 && displayHolidayYears.length === 0) {
        console.log('No display years or holiday years found for user');
        setUserSongs({});
        return;
      }

      // Fetch songs from both old and new systems
      const songPromises = [];

      // Songs from old display_years system
      if (displayYears.length > 0) {
        const displayYearIds = displayYears.map(dy => dy.id);
        songPromises.push(
          supabase
            .from('display_songs')
            .select('*')
            .in('display_year_id', displayYearIds)
            .order('created_at', { ascending: false })
        );
      }

      // Songs from new display_holiday_years system
      if (displayHolidayYears.length > 0) {
        const displayHolidayYearIds = displayHolidayYears.map(dhy => dhy.id);
        songPromises.push(
          supabase
            .from('display_songs')
            .select('*')
            .in('display_holiday_year_id', displayHolidayYearIds)
            .order('created_at', { ascending: false })
        );
      }

      const songResults = await Promise.all(songPromises);

      // Merge all songs from both systems
      const allSongs: any[] = [];
      songResults.forEach(result => {
        if (result.error) {
          console.error('Error fetching songs:', result.error);
        } else if (result.data) {
          allSongs.push(...result.data);
        }
      });

      // Deduplicate songs (in case same song is in both systems)
      const uniqueSongs = Array.from(
        new Map(allSongs.map(song => [song.id, song])).values()
      );

      // Group songs by year
      const songsByYear: Record<number, LocalSong[]> = {};

      uniqueSongs.forEach(song => {
        const year = song.year_introduced;
        if (!songsByYear[year]) {
          songsByYear[year] = [];
        }

        songsByYear[year].push({
          id: song.id,
          title: song.title,
          artist: song.artist,
          duration: song.duration || '0:00',
          year: song.year_introduced,
          genre: song.genre || undefined,
          albumCover: song.album_cover_url || undefined,
          album_cover_url: song.album_cover_url || undefined,
          videoUrl: song.video_url || undefined
        });
      });

      console.log('Fetched user songs (from display_years and display_holiday_years):', songsByYear);
      setUserSongs(songsByYear);
    } catch (error) {
      console.error('Unexpected error fetching songs:', error);
      setUserSongs({});
    }
  };

  const fetchUserSequences = async (userId: string) => {
    try {
      console.log('Fetching sequences for userId:', userId);
      const creatorData = await getCreatorSequences(userId);
      setUserSequences(creatorData.sequences);
      console.log('Fetched user sequences:', creatorData.sequences);
    } catch (error) {
      console.error('Error fetching user sequences:', error);
      setUserSequences([]);
    }
  };

  const handleEditProfile = () => {
    setIsEditing(true);
  };

  const handleSaveProfile = async (values: ProfileFormValues) => {
    if (!currentProfile) return;

    try {
      const { error } = await supabase
        .from('user_profiles')
        .update({
          display_name: values.fullName,
          bio: values.bio,
          username: values.username,
          social_links: values.socialLinks
        })
        .eq('user_id', currentProfile.user_id);

      if (error) {
        console.error('Error updating profile:', error);
        toast.error('Failed to update profile');
        return;
      }

      // Refresh the profile data after update
      if (!isPublicView) {
        await refreshProfile();
      }

      toast.success('Profile updated successfully');
      setIsEditing(false);
    } catch (error) {
      console.error('Error saving profile:', error);
      toast.error('Failed to update profile');
    }
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
  };

  const handleDeleteDisplay = async () => {
    if (!userDisplay) return;
    
    try {
      const { error } = await supabase
        .from('displays')
        .delete()
        .eq('id', userDisplay.id);

      if (error) {
        console.error('Error deleting display:', error);
        toast.error('Failed to delete display');
        return;
      }

      setUserDisplay(null);
      toast.success('Display deleted successfully');
    } catch (error) {
      console.error('Error deleting display:', error);
      toast.error('Failed to delete display');
    }
  };

  const handleSongAdded = (newSong: LocalSong) => {
    if (!newSong.year) return;
    
    setUserSongs(prev => ({
      ...prev,
      [newSong.year!]: [...(prev[newSong.year!] || []), newSong]
    }));
  };

  const handleSongDeleted = (songId: string, year: number) => {
    setUserSongs(prev => ({
      ...prev,
      [year]: prev[year]?.filter(song => song.id !== songId) || []
    }));
  };

  // Charity handlers
  const handleEditCharity = () => {
    setIsEditingCharity(true);
  };

  const handleCharitySaved = (savedCharity: Charity) => {
    setCharity(savedCharity);
    setIsEditingCharity(false);
  };

  const handleCancelEditCharity = () => {
    setIsEditingCharity(false);
  };

  // Handle tab change with unsaved changes check
  const handleTabChange = (newTab: string) => {
    if (navigationCheckRef.current && activeTab === "display") {
      navigationCheckRef.current(() => setActiveTab(newTab));
    } else {
      setActiveTab(newTab);
    }
  };

  const handleRequestNavigation = (callback: (proceed: () => void) => void) => {
    navigationCheckRef.current = callback;
  };

  // Loading states
  if (isLoading || isProfileLoading) {
    return (
      <div className="min-h-screen flex flex-col">
        <NavBar />
        <main className="grow flex items-center justify-center">
          <div className="animate-pulse text-xl">
            {isPublicView ? 'Loading profile...' : 'Loading your profile...'}
          </div>
        </main>
      </div>
    );
  }

  if (!currentProfile) {
    return (
      <div className="min-h-screen flex flex-col">
        <NavBar />
        <main className="grow flex items-center justify-center">
          <div className="text-xl text-gray-600">Profile not found</div>
        </main>
      </div>
    );
  }

  const totalSongs = Object.values(userSongs).reduce((total, yearSongs) => total + yearSongs.length, 0);

  return (
    <div className="min-h-screen flex flex-col">
      <NavBar />
      <main className="grow container mx-auto px-4 pt-24 pb-8">
        <div className="max-w-6xl mx-auto">
          {isEditing ? (
            <UserProfileForm
              user={currentProfile}
              onSave={handleSaveProfile}
              onCancel={handleCancelEdit}
            />
          ) : (
            <UserProfileHeader 
              user={currentProfile}
              userDisplay={userDisplay}
              onEditProfile={handleEditProfile}
              isPublicView={isPublicView}
            />
          )}

          {!isEditing && (
            <Tabs value={activeTab} onValueChange={handleTabChange} className="mt-8">
              <TabsList className="mb-6">
                {isPublicView ? (
                  <>
                    <TabsTrigger value="display">Display Info</TabsTrigger>
                    <TabsTrigger value="videos">Light Show Videos</TabsTrigger>
                    <TabsTrigger value="sequences">Sequences</TabsTrigger>
                    <TabsTrigger value="checkins">Check-ins</TabsTrigger>
                  </>
                ) : (
                  <>
                    <TabsTrigger value="display" className="relative">
                      My Display
                      {isOwner && !tabCounts.hasDisplay && !isCountsLoading && (
                        <span className="absolute -top-1 -right-1 w-2 h-2 bg-red-500 rounded-full animate-pulse"></span>
                      )}
                    </TabsTrigger>
                    <TabsTrigger value="sequences" className="relative">
                      My Sequences {!isCountsLoading && `(${tabCounts.sequenceCount})`}
                      {isOwner && tabCounts.sequenceCount === 0 && !isCountsLoading && (
                        <span className="absolute -top-1 -right-1 w-2 h-2 bg-red-500 rounded-full animate-pulse"></span>
                      )}
                    </TabsTrigger>
                    <TabsTrigger value="songs">
                      My Songs {!isCountsLoading && `(${tabCounts.songCount})`}
                    </TabsTrigger>
                    <TabsTrigger value="checkins">My Check-ins</TabsTrigger>
                    <TabsTrigger value="charity">Charity</TabsTrigger>
                  </>
                )}
              </TabsList>
              
              {isPublicView ? (
                <>
                  <TabsContent value="videos" className="mt-0 data-[state=inactive]:hidden" forceMount>
                    <PublicSongsTab
                      userSongs={userSongs}
                      profileUser={currentProfile}
                      totalSongs={totalSongs}
                    />
                  </TabsContent>

                  <TabsContent value="sequences" className="mt-0 data-[state=inactive]:hidden" forceMount>
                    <ProfileSequencesTab
                      userId={currentProfile.user_id}
                      username={currentProfile.username}
                      isOwner={false}
                      isPublicView={true}
                    />
                  </TabsContent>

                  <TabsContent value="checkins" className="mt-0 data-[state=inactive]:hidden" forceMount>
                    <CheckInsTab
                      userId={currentProfile.user_id}
                      isOwner={false}
                      isPublicView={true}
                    />
                  </TabsContent>

                  <TabsContent value="display" className="mt-0 data-[state=inactive]:hidden" forceMount>
                    <DisplayTab
                      userDisplay={userDisplay}
                      onDeleteDisplay={() => {}} // Read-only in public view
                      currentUserId={currentProfile.user_id}
                      isOwner={false}
                    />
                  </TabsContent>
                </>
              ) : (
                <>
                  <TabsContent value="display" className="mt-0 data-[state=inactive]:hidden" forceMount>
                    <DisplayTab
                      userDisplay={userDisplay}
                      onDeleteDisplay={handleDeleteDisplay}
                      currentUserId={currentProfile.user_id}
                      isOwner={isOwner}
                      onRequestNavigation={handleRequestNavigation}
                    />
                  </TabsContent>

                  <TabsContent value="sequences" className="mt-0 data-[state=inactive]:hidden" forceMount>
                    <ProfileSequencesTab
                      userId={currentProfile.user_id}
                      username={currentProfile.username}
                      isOwner={true}
                      isPublicView={false}
                    />
                  </TabsContent>

                  <TabsContent value="songs" className="mt-0 data-[state=inactive]:hidden" forceMount>
                    <SongsTab
                      userSongs={userSongs}
                      displayId={userDisplay?.id?.toString() || ''}
                      onSongAdded={handleSongAdded}
                      onSongDeleted={handleSongDeleted}
                    />
                  </TabsContent>

                  <TabsContent value="checkins" className="mt-0 data-[state=inactive]:hidden" forceMount>
                    <CheckInsTab
                      userId={currentProfile.user_id}
                      isOwner={isOwner}
                      isPublicView={false}
                    />
                  </TabsContent>

                  <TabsContent value="charity" className="mt-0 data-[state=inactive]:hidden" forceMount>
                    <CharityTab
                      charity={charity}
                      userId={currentProfile.user_id}
                      isEditingCharity={isEditingCharity}
                      onEditCharity={handleEditCharity}
                      onCharitySaved={handleCharitySaved}
                      onCancelEditCharity={handleCancelEditCharity}
                    />
                  </TabsContent>
                </>
              )}
            </Tabs>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default UserProfile;
